/**
 * Data Models & Enums
 * (Kotlin 코드의 외부 의존성 모델들을 정의)
 */
export enum StorageType {
  GoogleDrive = 'GoogleDrive',
  OneDrive = 'OneDrive',
  DirectCloud = 'DirectCloud',
  GIGAPOD = 'GIGAPOD',
  OfficeMailStorage = 'OfficeMailStorage',
  Other = 'Other',
}

export interface CloudShareLink {
  storageType: StorageType
  name: string
  url: string
  size: number
}

export interface AttachmentLinkShareOptions {
  getViewingExpireDate(): string | null
  visitLimit: number
}

/**
 * Android Context/Resources 의존성을 제거하기 위한 인터페이스
 * 실제 사용하는 곳에서 이 인터페이스를 구현하여 텍스트와 포맷팅을 제공해야 합니다.
 */
export interface IResourceProvider {
  getString(key: string): string
  getQuantityText(key: string, quantity: number): string
  formatSize(size: number): string
}

/**
 * Utility for file names (Replacement for Apache Commons FilenameUtils)
 */
const FileUtils = {
  getExtension: (filename: string | null): string | null => {
    if (!filename) return null
    const parts = filename.split('.')
    return parts.length > 1 ? parts.pop()!.toLowerCase() : null
  },
}

/**
 * Main Interface
 */
export interface SharingFilesHtmlTemplate {
  getHtmlTemplate(): string
  showOnlyJiranCloudItem(): boolean
}

/**
 * Companion Object Logic / Factory
 */
export class SharingFilesFactory {
  static readonly TEMPLATE_START = '<br><div class="rework-sharing-html-template">\n'
  static readonly TEMPLATE_END = '</div>\n\n'

  static createSharingAttachment(
    resources: IResourceProvider,
    linkList: CloudShareLink[],
    options: AttachmentLinkShareOptions | null,
    noImage: boolean = false,
  ): SharingFilesHtmlTemplate {
    if (noImage) {
      return new SharingFilesSimpleHtmlTemplate(resources, linkList, options)
    }
    return new SharingFilesRichHtmlTemplate(resources, linkList, options)
  }

  static removeHtmlTemplate(html: string | null): string {
    if (!html) return ''

    // Browser environment
    if (typeof DOMParser !== 'undefined') {
      const parser = new DOMParser()
      const doc = parser.parseFromString(html, 'text/html')
      const found = doc.querySelector('div.rework-sharing-html-template')
      if (found) {
        found.remove()
        return doc.body.innerHTML
      }
      return html
    }

    // Regex Fallback (Node.js or non-DOM environment)
    // Note: Simple regex implementation, might need adjustment based on exact HTML structure guarantees
    const regex = /[\s\S]*?/g
    return html.replace(regex, '')
  }
}

/**
 * Rich HTML Template Implementation
 */
export class SharingFilesRichHtmlTemplate implements SharingFilesHtmlTemplate {
  // Constants
  private static readonly LINE_START =
    '<div style="width:100%; margin-right:auto; color:#263e58; font-family:Segoe UI, Arial, sans-serif;">'
  private static readonly LINE_END = '</div>\n'
  private static readonly TABLE_START =
    '<table style="width:100%; font-size:14px; padding:0px 0px 20px 0px;" border="0">\n<tbody>\n'
  private static readonly TABLE_END = '</tbody>\n</table>\n'

  private static readonly BLANK_IMAGE_WIDTH = 18
  private static readonly LOGO_BLANK_IMAGE_WIDTH = 20
  private static readonly STATIC_ICON_ROOT = 'https://www.officemail.app/resources/fileicon/'

  // Icon Names
  private static readonly ICONS = {
    DOWNLOAD_CLOUD: 'download-cloud.png',
    ACCESSORY_DIRECTCLOUD: 'sharing-directcloud.png',
    ACCESSORY_GIGAPOD: 'sharing-gigapod.png',
    ACCESSORY_GOOGLE_DRIVE: 'sharing-google-drive.png',
    ACCESSORY_ONEDRIVE: 'sharing-onedrive.png',
    FILE_ARCHIVE: 'file-archive.png',
    FILE_AUDIO: 'file-audio.png',
    FILE_CODE: 'file-code.png',
    FILE_EXCEL: 'file-excel.png',
    FILE_GENERAL: 'file-general.png',
    FILE_HWP: 'file-hwp.png',
    FILE_IMAGE: 'file-image.png',
    FILE_PDF: 'file-pdf.png',
    FILE_PHOTOSHOP: 'file-photoshop.png',
    FILE_POWERPOINT: 'file-powerpoint.png',
    FILE_TEXT: 'file-text.png',
    FILE_VIDEO: 'file-video.png',
    FILE_WORD: 'file-word.png',
  }

  // Extension Mapping
  private static readonly DRAWABLE_MAPS: Record<string, string> = {
    // WORD
    doc: SharingFilesRichHtmlTemplate.ICONS.FILE_WORD,
    docx: SharingFilesRichHtmlTemplate.ICONS.FILE_WORD,
    dot: SharingFilesRichHtmlTemplate.ICONS.FILE_WORD,
    dotx: SharingFilesRichHtmlTemplate.ICONS.FILE_WORD,
    docm: SharingFilesRichHtmlTemplate.ICONS.FILE_WORD,
    dotm: SharingFilesRichHtmlTemplate.ICONS.FILE_WORD,
    rtf: SharingFilesRichHtmlTemplate.ICONS.FILE_WORD,
    // PPT
    ppt: SharingFilesRichHtmlTemplate.ICONS.FILE_POWERPOINT,
    ppa: SharingFilesRichHtmlTemplate.ICONS.FILE_POWERPOINT,
    pptx: SharingFilesRichHtmlTemplate.ICONS.FILE_POWERPOINT,
    potx: SharingFilesRichHtmlTemplate.ICONS.FILE_POWERPOINT,
    ppsx: SharingFilesRichHtmlTemplate.ICONS.FILE_POWERPOINT,
    ppam: SharingFilesRichHtmlTemplate.ICONS.FILE_POWERPOINT,
    pptm: SharingFilesRichHtmlTemplate.ICONS.FILE_POWERPOINT,
    potm: SharingFilesRichHtmlTemplate.ICONS.FILE_POWERPOINT,
    ppsm: SharingFilesRichHtmlTemplate.ICONS.FILE_POWERPOINT,
    pot: SharingFilesRichHtmlTemplate.ICONS.FILE_POWERPOINT,
    pps: SharingFilesRichHtmlTemplate.ICONS.FILE_POWERPOINT,
    // XLS
    xls: SharingFilesRichHtmlTemplate.ICONS.FILE_EXCEL,
    xlsx: SharingFilesRichHtmlTemplate.ICONS.FILE_EXCEL,
    xlsm: SharingFilesRichHtmlTemplate.ICONS.FILE_EXCEL,
    xlw: SharingFilesRichHtmlTemplate.ICONS.FILE_EXCEL,
    xla: SharingFilesRichHtmlTemplate.ICONS.FILE_EXCEL,
    xlc: SharingFilesRichHtmlTemplate.ICONS.FILE_EXCEL,
    xlm: SharingFilesRichHtmlTemplate.ICONS.FILE_EXCEL,
    xlt: SharingFilesRichHtmlTemplate.ICONS.FILE_EXCEL,
    // HWP
    hwp: SharingFilesRichHtmlTemplate.ICONS.FILE_HWP,
    hwpx: SharingFilesRichHtmlTemplate.ICONS.FILE_HWP,
    // Code
    css: SharingFilesRichHtmlTemplate.ICONS.FILE_CODE,
    htm: SharingFilesRichHtmlTemplate.ICONS.FILE_CODE,
    html: SharingFilesRichHtmlTemplate.ICONS.FILE_CODE,
    java: SharingFilesRichHtmlTemplate.ICONS.FILE_CODE,
    js: SharingFilesRichHtmlTemplate.ICONS.FILE_CODE,
    py: SharingFilesRichHtmlTemplate.ICONS.FILE_CODE,
    // Image
    png: SharingFilesRichHtmlTemplate.ICONS.FILE_IMAGE,
    jpg: SharingFilesRichHtmlTemplate.ICONS.FILE_IMAGE,
    jpeg: SharingFilesRichHtmlTemplate.ICONS.FILE_IMAGE,
    bmp: SharingFilesRichHtmlTemplate.ICONS.FILE_IMAGE,
    gif: SharingFilesRichHtmlTemplate.ICONS.FILE_IMAGE,
    heic: SharingFilesRichHtmlTemplate.ICONS.FILE_IMAGE,
    heif: SharingFilesRichHtmlTemplate.ICONS.FILE_IMAGE,
    raw: SharingFilesRichHtmlTemplate.ICONS.FILE_IMAGE,
    svg: SharingFilesRichHtmlTemplate.ICONS.FILE_IMAGE,
    tiff: SharingFilesRichHtmlTemplate.ICONS.FILE_IMAGE,
    // Audio
    aac: SharingFilesRichHtmlTemplate.ICONS.FILE_AUDIO,
    aiff: SharingFilesRichHtmlTemplate.ICONS.FILE_AUDIO,
    flac: SharingFilesRichHtmlTemplate.ICONS.FILE_AUDIO,
    m4a: SharingFilesRichHtmlTemplate.ICONS.FILE_AUDIO,
    mp3: SharingFilesRichHtmlTemplate.ICONS.FILE_AUDIO,
    ogg: SharingFilesRichHtmlTemplate.ICONS.FILE_AUDIO,
    wav: SharingFilesRichHtmlTemplate.ICONS.FILE_AUDIO,
    // Video
    mp4: SharingFilesRichHtmlTemplate.ICONS.FILE_VIDEO,
    mov: SharingFilesRichHtmlTemplate.ICONS.FILE_VIDEO,
    mpeg: SharingFilesRichHtmlTemplate.ICONS.FILE_VIDEO,
    mpg: SharingFilesRichHtmlTemplate.ICONS.FILE_VIDEO,
    wmv: SharingFilesRichHtmlTemplate.ICONS.FILE_VIDEO,
    flv: SharingFilesRichHtmlTemplate.ICONS.FILE_VIDEO,
    mkv: SharingFilesRichHtmlTemplate.ICONS.FILE_VIDEO,
    '3gp': SharingFilesRichHtmlTemplate.ICONS.FILE_VIDEO,
    avi: SharingFilesRichHtmlTemplate.ICONS.FILE_VIDEO,
    // PDF
    pdf: SharingFilesRichHtmlTemplate.ICONS.FILE_PDF,
    // Photoshop
    psd: SharingFilesRichHtmlTemplate.ICONS.FILE_PHOTOSHOP,
    eps: SharingFilesRichHtmlTemplate.ICONS.FILE_PHOTOSHOP,
    // Text
    txt: SharingFilesRichHtmlTemplate.ICONS.FILE_TEXT,
    log: SharingFilesRichHtmlTemplate.ICONS.FILE_TEXT,
    md: SharingFilesRichHtmlTemplate.ICONS.FILE_TEXT,
    // Archive
    '7z': SharingFilesRichHtmlTemplate.ICONS.FILE_ARCHIVE,
    apk: SharingFilesRichHtmlTemplate.ICONS.FILE_ARCHIVE,
    cab: SharingFilesRichHtmlTemplate.ICONS.FILE_ARCHIVE,
    dmg: SharingFilesRichHtmlTemplate.ICONS.FILE_ARCHIVE,
    gz: SharingFilesRichHtmlTemplate.ICONS.FILE_ARCHIVE,
    jar: SharingFilesRichHtmlTemplate.ICONS.FILE_ARCHIVE,
    kgb: SharingFilesRichHtmlTemplate.ICONS.FILE_ARCHIVE,
    pea: SharingFilesRichHtmlTemplate.ICONS.FILE_ARCHIVE,
    rar: SharingFilesRichHtmlTemplate.ICONS.FILE_ARCHIVE,
    zip: SharingFilesRichHtmlTemplate.ICONS.FILE_ARCHIVE,
    zipx: SharingFilesRichHtmlTemplate.ICONS.FILE_ARCHIVE,
  }

  constructor(
    private resources: IResourceProvider,
    private linkList: CloudShareLink[],
    private options: AttachmentLinkShareOptions | null,
  ) {}

  getHtmlTemplate(): string {
    let sb = ''
    sb += SharingFilesFactory.TEMPLATE_START
    sb += SharingFilesRichHtmlTemplate.LINE_START + SharingFilesRichHtmlTemplate.TABLE_START
    sb += this.getTitle()

    this.linkList.forEach((link) => {
      sb += this.getLink(link)
    })

    if (this.showOnlyJiranCloudItem()) {
      sb += this.getShareOptions()
    } else {
      sb += this.getLine()
    }

    sb += SharingFilesRichHtmlTemplate.TABLE_END + SharingFilesRichHtmlTemplate.LINE_END
    sb += SharingFilesFactory.TEMPLATE_END

    return sb
  }

  showOnlyJiranCloudItem(): boolean {
    // Logic duplicated to match Kotlin implementation structure,
    // normally this should be a shared utility function.
    const googleDriveItems = this.linkList.filter(
      (it) => it.storageType === StorageType.GoogleDrive || it.storageType === StorageType.OneDrive,
    )
    const directCloudItems = this.linkList.filter(
      (it) => it.storageType === StorageType.DirectCloud,
    )
    const gigapodItems = this.linkList.filter((it) => it.storageType === StorageType.GIGAPOD)
    const officeMailStorageItems = this.linkList.filter(
      (it) => it.storageType === StorageType.OfficeMailStorage,
    )

    const onlyGoogleItemsExist =
      googleDriveItems.length > 0 && directCloudItems.length === 0 && gigapodItems.length === 0
    const onlyDirectCloudItemsExist =
      directCloudItems.length > 0 && googleDriveItems.length === 0 && gigapodItems.length === 0
    const onlyGigapodItemsExist =
      gigapodItems.length > 0 && googleDriveItems.length === 0 && directCloudItems.length === 0
    const onlyOfficeMailStorageExist =
      officeMailStorageItems.length > 0 &&
      googleDriveItems.length === 0 &&
      directCloudItems.length === 0 &&
      gigapodItems.length === 0

    const nonEmptyListCount = [
      googleDriveItems,
      directCloudItems,
      gigapodItems,
      officeMailStorageItems,
    ].filter((it) => it.length > 0).length

    if (nonEmptyListCount >= 2) {
      return false
    } else {
      if (onlyDirectCloudItemsExist || onlyGigapodItemsExist || onlyOfficeMailStorageExist) {
        return true
      } else if (onlyGoogleItemsExist) {
        return false
      }
      return false
    }
  }

  private getTitle(): string {
    let totalSize = 0
    this.linkList.forEach((it) => (totalSize += it.size))

    return `<tr>
<td style="padding:0px 10px;">
<img src="${this.getIconPath(SharingFilesRichHtmlTemplate.ICONS.DOWNLOAD_CLOUD)}" style="padding-right:5px; padding-bottom:5px; border:0; vertical-align:middle;" height="${SharingFilesRichHtmlTemplate.LOGO_BLANK_IMAGE_WIDTH}" width="${SharingFilesRichHtmlTemplate.LOGO_BLANK_IMAGE_WIDTH}">
<span style="font-weight:bold; vertical-align:middle;"> ${this.resources.getQuantityText('sharing_files_quantity', this.linkList.length)} - ${this.resources.formatSize(totalSize)}</span>
</td>
</tr>`
  }

  private getShareOptions(): string {
    const visitLimitStr =
      (this.options?.visitLimit || 0) > 0
        ? String(this.options?.visitLimit)
        : this.resources.getString('unlimited')

    return `<tr>
<td style="padding-left:10px; vertical-align:middle;">
<hr style="border:solid 0.5px lightgray;"/>
</tr><tr>
<td style="padding-bottom:10px; padding-left:10px; vertical-align:middle; border-bottom:solid 0.5px lightgray; color:gray;">
<span>${this.resources.getString('expire_date')} : ${this.options?.getViewingExpireDate() || this.resources.getString('no_limit')}</span>, <span>${this.resources.getString('visit_limit')} : ${visitLimitStr}</span>
</td>
</tr>`
  }

  private getLine(): string {
    return `<tr>
<td style="padding-left:10px; vertical-align:middle;">
<hr style="border:solid 0.5px lightgray;"/>
</tr>`
  }

  private getLink(link: CloudShareLink): string {
    return `<tr>
<td style="padding:5px 10px; vertical-align:middle;">
<img style="border:0; vertical-align:middle" height="${SharingFilesRichHtmlTemplate.BLANK_IMAGE_WIDTH}" width="${SharingFilesRichHtmlTemplate.BLANK_IMAGE_WIDTH}" src="${this.getCloudTypeIconPath(link)}">
<img src="${this.getIconPath(this.getFileTypeIcon(link.name))}" style="border:0; vertical-align:middle;" height="${SharingFilesRichHtmlTemplate.BLANK_IMAGE_WIDTH}" width="${SharingFilesRichHtmlTemplate.BLANK_IMAGE_WIDTH}">
<span style="padding-left:5px; vertical-align: middle"><a href="${link.url}" target="_BLANK" style="text-decoration:none; color:#262626; padding-right:5px; vertical-align:middle">${link.name}</a></span>
<span style="color:gray; vertical-align: middle">(${this.resources.formatSize(link.size)})</span>
</td>
</tr>
`
  }

  private getCloudTypeIconPath(link: CloudShareLink): string {
    switch (link.storageType) {
      case StorageType.OneDrive:
        return this.getIconPath(SharingFilesRichHtmlTemplate.ICONS.ACCESSORY_ONEDRIVE)
      case StorageType.GoogleDrive:
        return this.getIconPath(SharingFilesRichHtmlTemplate.ICONS.ACCESSORY_GOOGLE_DRIVE)
      case StorageType.DirectCloud:
        return this.getIconPath(SharingFilesRichHtmlTemplate.ICONS.ACCESSORY_DIRECTCLOUD)
      case StorageType.GIGAPOD:
        return this.getIconPath(SharingFilesRichHtmlTemplate.ICONS.ACCESSORY_GIGAPOD)
      case StorageType.OfficeMailStorage:
        return this.getIconPath(SharingFilesRichHtmlTemplate.ICONS.DOWNLOAD_CLOUD)
      default:
        return '' // Fallback or empty
    }
  }

  public getFileTypeIcon(name: string | null): string {
    const extension = FileUtils.getExtension(name)
    if (extension) {
      return (
        SharingFilesRichHtmlTemplate.DRAWABLE_MAPS[extension] ||
        SharingFilesRichHtmlTemplate.ICONS.FILE_GENERAL
      )
    }
    return SharingFilesRichHtmlTemplate.ICONS.FILE_GENERAL
  }

  public getIconPath(fileName: string): string {
    return SharingFilesRichHtmlTemplate.STATIC_ICON_ROOT + fileName
  }
}

/**
 * Simple HTML Template Implementation
 */
export class SharingFilesSimpleHtmlTemplate implements SharingFilesHtmlTemplate {
  private static readonly OFFICE_NOTE_ATTACHMENT_ICON = 'sharing-officenote.png'

  constructor(
    private resources: IResourceProvider,
    private linkList: CloudShareLink[],
    private options: AttachmentLinkShareOptions | null,
  ) {}

  getHtmlTemplate(): string {
    let sb = ''
    // sb += SharingFilesFactory.TEMPLATE_START; // Commented out in original
    sb += this.getTitle()

    this.linkList.forEach((link) => {
      sb += this.getLink(link)
    })

    if (this.showOnlyJiranCloudItem()) {
      sb += this.getShareOptions()
    } else {
      sb += this.getLine()
    }
    // sb += SharingFilesFactory.TEMPLATE_END; // Commented out in original

    return sb
  }

  showOnlyJiranCloudItem(): boolean {
    const googleDriveItems = this.linkList.filter(
      (it) => it.storageType === StorageType.GoogleDrive || it.storageType === StorageType.OneDrive,
    )
    const directCloudItems = this.linkList.filter(
      (it) => it.storageType === StorageType.DirectCloud,
    )
    const gigapodItems = this.linkList.filter((it) => it.storageType === StorageType.GIGAPOD)
    const officeMailStorageItems = this.linkList.filter(
      (it) => it.storageType === StorageType.OfficeMailStorage,
    )

    const onlyGoogleItemsExist =
      googleDriveItems.length > 0 &&
      directCloudItems.length === 0 &&
      gigapodItems.length === 0 &&
      officeMailStorageItems.length === 0
    const onlyDirectCloudItemsExist =
      directCloudItems.length > 0 &&
      googleDriveItems.length === 0 &&
      gigapodItems.length === 0 &&
      officeMailStorageItems.length === 0
    const onlyGigapodItemsExist =
      gigapodItems.length > 0 &&
      googleDriveItems.length === 0 &&
      directCloudItems.length === 0 &&
      officeMailStorageItems.length === 0
    const onlyOfficeMailStorageExist =
      officeMailStorageItems.length > 0 &&
      googleDriveItems.length === 0 &&
      directCloudItems.length === 0 &&
      gigapodItems.length === 0

    const nonEmptyListCount = [
      googleDriveItems,
      directCloudItems,
      gigapodItems,
      officeMailStorageItems,
    ].filter((it) => it.length > 0).length

    if (nonEmptyListCount >= 2) {
      return false
    } else {
      if (onlyDirectCloudItemsExist || onlyGigapodItemsExist || onlyOfficeMailStorageExist) {
        return true
      } else if (onlyGoogleItemsExist) {
        return false
      }
      return false
    }
  }

  private getTitle(): string {
    let totalSize = 0
    this.linkList.forEach((it) => (totalSize += it.size))

    return `<b>${this.linkList.length} ${this.resources.getQuantityText('sharing_files_quantity', this.linkList.length)} - ${this.resources.formatSize(totalSize)}</b><br>`
  }

  private getLink(link: CloudShareLink): string {
    return `<a href="${link.url}" target="_BLANK">☁️ ${link.name}(${this.resources.formatSize(link.size)})</a><br>`
  }

  private getShareOptions(): string {
    if (!this.options) return ''

    const visitLimitStr =
      this.options.visitLimit > 0 ? this.options.visitLimit : this.resources.getString('unlimited')

    return `----------<br><i>${this.resources.getString('expire_date')} : ${this.options.getViewingExpireDate() || this.resources.getString('no_limit')}, ${this.resources.getString('visit_limit')} : ${visitLimitStr}<br>----------<br><span>&nbsp;</span>`
  }

  private getLine(): string {
    return '----------<br>'
  }

  static getIconPath(fileName: string): string {
    return `https://www.officemail.app/resources/fileicon/${fileName}`
  }
}
